import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Plus, Trash2, GraduationCap, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, V1 } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const GRADES = ["1","2","3","4","5","6","7","8","9","10","11","12"];
const EXAM_TYPES = ["LGS","TYT","AYT"];

export default function ParentChildren() {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", surname: "", grade_level: "", exam_type: "", target_city: "", target_school: "", target_department: "" });

  const load = () => api.get(`${V1}/children`).then((r) => setChildren(r.data || []));
  // Only parents may call /children (endpoint requires role='parent').
  // Students self-manage — they get redirected below; do NOT fire the API for them.
  useEffect(() => {
    if (user && user.role === "parent") load();
  }, [user]);

  // Student self-service users don't manage a child list — they ARE the student.
  // Send them to their overview which shows their own profile & performance.
  if (user && user.role === "student") return <Navigate to="/panel" replace />;

  const create = async () => {
    if (!form.name || !form.surname) return toast.error("Ad ve soyad zorunlu");
    try {
      await api.post(`${V1}/children`, form);
      toast.success("Öğrenci eklendi");
      setOpen(false);
      setForm({ name: "", surname: "", grade_level: "", exam_type: "", target_city: "", target_school: "", target_department: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eklenemedi");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Öğrenci profili silinsin mi?")) return;
    await api.delete(`${V1}/children/${id}`);
    load();
  };

  const toggleDataSharing = async (id, value) => {
    try {
      await api.patch(`${V1}/children/${id}`, { data_sharing_opt_in: value });
      setChildren((prev) => prev.map((c) => (c.id === id ? { ...c, data_sharing_opt_in: value } : c)));
      toast.success(value ? "İletişim bilgisi paylaşımına izin verildi" : "İzin geri çekildi");
    } catch (e) { toast.error(e?.response?.data?.detail || "Güncellenemedi"); }
  };

  return (
    <div className="space-y-6" data-testid="parent-children-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline text-accent">Çocuklarım</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Öğrenci Profilleri</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-child-btn"><Plus className="h-4 w-4" /> Öğrenci Ekle</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Öğrenci Profili Ekle</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-1">
                <Label>Ad</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="child-name" />
              </div>
              <div className="col-span-1">
                <Label>Soyad</Label>
                <Input value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })} data-testid="child-surname" />
              </div>
              <div>
                <Label>Sınıf</Label>
                <Select value={form.grade_level} onValueChange={(v) => setForm({ ...form, grade_level: v })}>
                  <SelectTrigger data-testid="child-grade"><SelectValue placeholder="Sınıf seç" /></SelectTrigger>
                  <SelectContent>
                    {GRADES.map((g) => <SelectItem key={g} value={g}>{g}. sınıf</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Hedef Sınav</Label>
                <Select value={form.exam_type} onValueChange={(v) => setForm({ ...form, exam_type: v })}>
                  <SelectTrigger data-testid="child-exam-type"><SelectValue placeholder="Sınav" /></SelectTrigger>
                  <SelectContent>
                    {EXAM_TYPES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Hedef Şehir / Okul / Bölüm</Label>
                <div className="grid grid-cols-3 gap-2">
                  <Input placeholder="Şehir" value={form.target_city} onChange={(e) => setForm({ ...form, target_city: e.target.value })} />
                  <Input placeholder="Okul" value={form.target_school} onChange={(e) => setForm({ ...form, target_school: e.target.value })} />
                  <Input placeholder="Bölüm" value={form.target_department} onChange={(e) => setForm({ ...form, target_department: e.target.value })} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Vazgeç</Button>
              <Button onClick={create} data-testid="save-child-btn">Kaydet</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="children-grid">
        {children.length === 0 && <div className="col-span-3 p-10 rounded-xl border border-dashed border-border text-center text-muted-foreground">Henüz öğrenci profili yok. Ekle butonuna tıklayın.</div>}
        {children.map((c) => (
          <div key={c.id} className="p-5 rounded-xl border border-border bg-card" data-testid={`child-${c.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="font-display font-bold text-lg">{c.name} {c.surname}</div>
                <div className="text-xs text-muted-foreground">{c.grade_level ? `${c.grade_level}. sınıf` : "Sınıf belirtilmedi"}</div>
              </div>
              <button onClick={() => del(c.id)} className="text-muted-foreground hover:text-destructive" data-testid={`del-child-${c.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            {c.exam_type && <div className="mt-3 text-xs"><span className="uppercase tracking-widest text-muted-foreground">Hedef Sınav:</span> <b>{c.exam_type}</b></div>}
            {c.target_school && <div className="text-xs"><span className="uppercase tracking-widest text-muted-foreground">Hedef Okul:</span> {c.target_school}</div>}
            <div className="mt-4 pt-3 border-t border-border flex items-start gap-2" data-testid={`data-sharing-row-${c.id}`}>
              <Switch
                checked={!!c.data_sharing_opt_in}
                onCheckedChange={(v) => toggleDataSharing(c.id, v)}
                data-testid={`data-sharing-toggle-${c.id}`}
              />
              <span className="text-[11px] text-muted-foreground leading-snug flex items-start gap-1">
                <Share2 className="h-3 w-3 mt-0.5 shrink-0" />
                Öğrenci Listesi Paketi alan okullar iletişim bilgimizi görebilsin (KVKK açık rıza, istediğim an geri çekebilirim)
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
